/**
 * Operations Report API — aggregated billing ops snapshot.
 */

import { apiFetch, getToken } from './client'

export interface OpsMetric {
  value: number | null
  priorValue: number | null
  trendPct: number | null
}

export interface OperationsReportPayload {
  filters: {
    period: string
    startDate: string
    endDate: string
    label: string
    projectId: string | null
    projectName?: string | null
    owner: string | null
  }
  metrics: {
    billed: OpsMetric
    collected: OpsMetric
    outstanding: OpsMetric
    retainageHeld: OpsMetric
    avgDaysToPaid: OpsMetric
  }
  aging: {
    bucket0_30: { count: number; amount: number }
    bucket31_60: { count: number; amount: number }
    bucket60plus: { count: number; amount: number }
    items: Array<Record<string, unknown>>
  }
  completeness: {
    unbilledCos: Array<Record<string, unknown>>
    retentionReady: Array<Record<string, unknown>>
    prelimsNotMailed: Array<Record<string, unknown>>
    openLienWindows: Array<Record<string, unknown>>
  }
  pendingPayments: Array<Record<string, unknown>>
  jobHealth: Array<Record<string, unknown>>
  filterOptions: Array<{ id: number; name: string; owner_name: string | null }>
  monthlyTrend?: Array<{ label: string; start: string; end: string; billed: number; collected: number }>
  openPayApps?: Array<{
    id: number
    project_id: number
    project_name: string
    app_number: number
    balance: number
    days_outstanding: number
  }>
  generatedAt: string
}

export interface OperationsQuery {
  period?: string
  startDate?: string
  endDate?: string
  projectId?: string
  owner?: string
}

function qs(params: OperationsQuery): string {
  const p = new URLSearchParams()
  if (params.period) p.set('period', params.period)
  if (params.startDate) p.set('start_date', params.startDate)
  if (params.endDate) p.set('end_date', params.endDate)
  if (params.projectId) p.set('project_id', params.projectId)
  if (params.owner) p.set('owner', params.owner)
  const s = p.toString()
  return s ? `?${s}` : ''
}

export async function fetchOperationsReport(params: OperationsQuery): Promise<OperationsReportPayload> {
  const res = await apiFetch<{ data: OperationsReportPayload }>('GET', `/api/reports/operations${qs(params)}`)
  return res.data
}

export async function exportOperationsReport(format: 'csv' | 'pdf', params: OperationsQuery): Promise<{ blob: Blob; filename: string }> {
  const p = new URLSearchParams(qs(params).replace(/^\?/, ''))
  p.set('format', format)
  const token = getToken()
  const res = await fetch(`/api/reports/operations/export?${p}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) throw new Error('Export failed')
  const cd = res.headers.get('Content-Disposition') || ''
  const match = cd.match(/filename="([^"]+)"/)
  const filename = match?.[1] || `operations-report.${format}`
  const blob = await res.blob()
  return { blob, filename }
}
